import { FlowMappings, ProcessAction } from './actions.dictionary'
import { RuntimeComponentDefinition } from './custom-component.dictionary'
import { TemplateConfigResponse } from './template-config.dictionary'
import { CollectionPrototypeInstanceMeta } from './template.dictionary'

export enum StartMode {
  MANUAL = 'manual',
  EMAIL_TRIGGER = 'emailTrigger',
  WEBHOOK_TRIGGER = 'webhookTrigger',
}

// Where a process start inside a UI flow originated, sent to the backend so it can
// audit the call distinctly: 'action' = a start_process UI action (Start Process),
// 'template' = a process-renderer UI template (Render Process). Both hit the same
// start endpoint, so this query param is the only discriminator.
export type UiFlowStartTrigger = 'action' | 'template'

export type ProcessDataValue = string | number | boolean | object | any[] | null
export type ProcessData = Record<string, ProcessDataValue>

/** Reserved root key on ProcessData carrying global UiFlowSession metadata
 * (uiFlowSessionUuid, workspaceId, projectId). Paths under this key are
 * exempt from collection-relative prefixing. */
export const ADDITIONAL_DATA_ROOT = 'additionalData'

// TODO: to (better) define this
export type TemplateConfigInterface = any

export type TemplateConfigInterfaceFromCollection = TemplateConfigInterface & {
  prototypeInstanceMeta?: CollectionPrototypeInstanceMeta[] | CollectionPrototypeInstanceMeta
}

export type TemplateSequence = {
  id: number
  context: string
  allowReadOnly?: boolean
}

export type ProgressUpdate = {
  currentNodeId: number
  processInstanceUuid: string
  tokenUuid: string
  nodeSequences: number[]
  currentContext: string
}

export enum ProcessState {
  CREATED = 'CREATED',
  STARTED = 'STARTED',
  FINISHED = 'FINISHED',
  FINISHED_WITH_ERROR = 'FINISHED_WITH_ERROR',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
  ABORTED = 'ABORTED',
  DISMISSED = 'DISMISSED',
  ON_HOLD = 'ON_HOLD',
  TERMINATED = 'TERMINATED',
  MIGRATING = 'MIGRATING',
}

export type ProcessStateUpdateDTO = {
  processInstanceUuid: string
  processState: ProcessState
}

export type ProgressUpdateDTO = {
  currentContext: string
  currentNodeId: number
  processInstanceUuid: string
  currentTemplateId: number
  tokenUuid: string
  nodeSequences: number[]
}

export type NodeDetailsResponse = {
  data: ProcessData
  actions: ProcessAction[]
  navigationTree: TemplateConfigResponse
  templateConfig: TemplateConfigResponse[]
  mappings: FlowMappings
  ccMappings?: FlowMappings
  // keyed by `${reusableUiTemplateContext}:${componentFlowxUuid}`
  ccRutMappings?: FlowMappings
  customComponents?: RuntimeComponentDefinition[]
}

export interface ProcessInstanceStateUpdate {
  uuid: string
  nodeDefinitionId: number
  tokenUuid: string
  currentContext: string
  actions: ProcessAction[]
  data: Record<string, any>
  navigationTree: TemplateConfigResponse
  templateConfig: TemplateConfigResponse[]
}

export type ProcessDataModel = {
  [key: string]: ProcessDataModelEntity
}

export type SubProcessDataModel = {
  [key: string]: ProcessDataModel
}

type ProcessDataModelEntity = {
  type: DataModelType
  metadata: ProcessDataModelMetadata | null
}

type ProcessDataModelMetadata = {
  numberOfDecimals: number
  reference?: {
    name: string
    appId: string
    resourceDefinitionId: string
    targetFlowxUuid: string
  }
}

export enum DataModelType {
  STRING = 'STRING',
  INTEGER = 'INTEGER',
  FLOAT = 'FLOAT',
  BOOLEAN = 'BOOLEAN',
  OBJECT = 'OBJECT',
  ENUM = 'ENUM',
  DATE_TIME = 'DATE_TIME',
  ZONED_DATE_TIME = 'ZONED_DATE_TIME',
  CURRENCY = 'CURRENCY',
}
